/**
 * @fileoverview Own Intake's selected-batch communication feed and explicit send
 * session. The backend returns selector summaries and only one batch's drafts;
 * IntakeCommunicationCard.js owns local editors. A send captures only the
 * included, displayed drafts and submits them sequentially through the existing
 * review API. Conflicts, failures, leaving the feed, or Stop remaining halt future
 * requests. Polling preserves reviewed text and editors; the snapping feed and
 * navigation track the conversation currently in view without changing it.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppIcon from '../shared/AppIcon';
import IntakeViewSwitch from './IntakeViewSwitch';
import IntakeCommunicationCard from './IntakeCommunicationCard';
import { fetchIntakeCommunication, fetchIntakeCommunications, reviewIntakeCommunication } from './intakeApi';
import './Intake.css';
import './IntakeCommunications.css';

const VIEWS = [['all', 'All'], ['needs_review', 'Needs review'], ['needs_staff', 'Needs staff'], ['waiting', 'Waiting'], ['sent', 'Sent']];
const EMPTY_BOARD = { batches: [], batch_options: [], selected_batch_key: null, counts: {}, total: 0 };
const errorText = (error) => error?.response?.data?.error || 'Communications are unavailable. Please try again.';
const draftKey = (detail) => `${detail.lead.id}:${detail.draft?.id}`;
const ready = (detail) => detail.conversation.view === 'needs_review' && detail.can_send && !detail.delivery_policy.error;
const batchDate = (value) => value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';

export default function IntakeCommunications() {
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some(([key]) => key === params.get('view')) ? params.get('view') : 'all';
  const scope = ['all', 'mine', 'unassigned'].includes(params.get('scope')) ? params.get('scope') : 'all';
  const search = params.get('search') || '';
  const batchKey = params.get('batch') || '';
  const [board, setBoard] = useState(EMPTY_BOARD);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [changed, setChanged] = useState({});
  const [errors, setErrors] = useState({});
  const [notices, setNotices] = useState({});
  const [batchNotices, setBatchNotices] = useState({});
  const [excluded, setExcluded] = useState({});
  const [editors, setEditors] = useState({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [updatesAvailable, setUpdatesAvailable] = useState(false);
  const [version, setVersion] = useState(0);
  const [searchText, setSearchText] = useState(search);
  const [currentConversation, setCurrentConversation] = useState(0);
  const feedScrollRef = useRef(null);
  const mounted = useRef(true);
  const epoch = useRef(0);
  const boardRef = useRef(board);
  const busyRef = useRef(false);
  const stopRef = useRef(false);
  const editorsRef = useRef({});
  const paramsRef = useRef(params);
  const setParamsRef = useRef(setParams);
  boardRef.current = board;
  paramsRef.current = params;
  setParamsRef.current = setParams;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; stopRef.current = true; epoch.current += 1; };
  }, []);

  const updateParams = useCallback((values) => {
    const next = new URLSearchParams(paramsRef.current);
    Object.entries(values).forEach(([key, value]) => value ? next.set(key, String(value)) : next.delete(key));
    setParamsRef.current(next, { replace: true });
  }, []);

  const onEditorChange = useCallback((id, state) => {
    const next = { ...editorsRef.current };
    if (state) next[id] = state; else delete next[id];
    editorsRef.current = next;
    if (mounted.current) setEditors(next);
  }, []);
  const canLeave = useCallback(() => !busyRef.current && (!Object.values(editorsRef.current).some((editor) => editor.dirty) || window.confirm('Discard your unsaved email changes?')), []);

  const loadBoard = useCallback(async (quiet = false) => {
    const requestEpoch = ++epoch.current;
    if (!quiet) setLoading(true);
    try {
      const response = await fetchIntakeCommunications({ layout: 'batches', view, scope, search,
        batch_key: (quiet ? boardRef.current.selected_batch_key : batchKey || boardRef.current.selected_batch_key) || undefined });
      if (!mounted.current || requestEpoch !== epoch.current || (quiet && busyRef.current)) return;
      const data = response.data;
      if (quiet) {
        const incoming = new Map(data.batches.flatMap((batch) => batch.conversations.map((detail) => [detail.lead.id, detail])));
        const current = boardRef.current.batches.flatMap((batch) => batch.conversations);
        const stale = current.filter((detail) => incoming.get(detail.lead.id)?.revision !== detail.revision);
        if (stale.length) setChanged((previous) => ({ ...previous, ...Object.fromEntries(stale.map((detail) => [detail.lead.id, true])) }));
        if (stale.length || incoming.size !== current.length) setUpdatesAvailable(true);
        const next = { ...boardRef.current, batch_options: data.batch_options };
        boardRef.current = next; setBoard(next);
      } else {
        setBoard(data); boardRef.current = data;
        setChanged({}); setErrors({}); setNotices({}); setBatchNotices({}); setUpdatesAvailable(false);
        setVersion((current) => current + 1);
      }
      setLoadError('');
    } catch (error) {
      if (mounted.current && requestEpoch === epoch.current) setLoadError(errorText(error));
    } finally {
      if (mounted.current && requestEpoch === epoch.current) setLoading(false);
    }
  }, [view, scope, search, batchKey]);

  useEffect(() => {
    stopRef.current = true;
    const pending = { ...EMPTY_BOARD, batch_options: boardRef.current.batch_options,
      selected_batch_key: batchKey || boardRef.current.selected_batch_key };
    setBoard(pending); boardRef.current = pending;
    editorsRef.current = {}; setEditors({});
    loadBoard();
    return () => { epoch.current += 1; stopRef.current = true; };
  }, [loadBoard, batchKey]);

  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden && !busyRef.current) loadBoard(true); }, 20000);
    return () => window.clearInterval(timer);
  }, [loadBoard]);
  useEffect(() => { setSearchText(search); }, [search]);
  useEffect(() => {
    if (searchText === search) return undefined;
    const timer = window.setTimeout(() => {
      if (canLeave()) updateParams({ search: searchText.trim(), batch: boardRef.current.selected_batch_key, page: null, lead: null }); else setSearchText(search);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchText, search, updateParams, canLeave]);

  useEffect(() => {
    const beforeUnload = (event) => {
      if (busyRef.current || Object.values(editorsRef.current).some((editor) => editor.dirty)) { event.preventDefault(); event.returnValue = ''; }
    };
    const navigation = (event) => {
      const anchor = event.target.closest('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.origin !== window.location.origin) return;
      if (!canLeave()) { event.preventDefault(); event.stopPropagation(); }
      else stopRef.current = true;
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', navigation, true);
    return () => { window.removeEventListener('beforeunload', beforeUnload); document.removeEventListener('click', navigation, true); };
  }, [canLeave]);

  const replaceDetail = (data) => {
    const previous = boardRef.current.batches.flatMap((batch) => batch.conversations).find((detail) => detail.lead.id === data.lead.id);
    const next = { ...boardRef.current, counts: { ...boardRef.current.counts }, batches: boardRef.current.batches.map((batch) => ({
      ...batch, conversations: batch.conversations.map((detail) => detail.lead.id === data.lead.id ? data : detail),
    })) };
    if (previous && previous.conversation.view !== data.conversation.view) {
      next.counts[previous.conversation.view] = Math.max(0, (next.counts[previous.conversation.view] || 0) - 1);
      next.counts[data.conversation.view] = (next.counts[data.conversation.view] || 0) + 1;
    }
    boardRef.current = next;
    setBoard(next);
  };

  const performAction = async (snapshot, action, form, batchKey) => {
    const id = snapshot.lead.id;
    const actionEpoch = epoch.current;
    setErrors((current) => ({ ...current, [id]: '' }));
    setNotices((current) => ({ ...current, [id]: '' }));
    try {
      const response = await reviewIntakeCommunication(id, {
        action, revision: snapshot.revision, message_id: snapshot.draft?.id,
        ...(batchKey ? { batch_key: batchKey } : {}), ...(action === 'save' ? form : {}),
      });
      if (!mounted.current || actionEpoch !== epoch.current) return null;
      const data = response.data;
      replaceDetail(data);
      setChanged((current) => ({ ...current, [id]: false }));
      const message = action === 'save' ? 'Draft saved.' : action === 'hold' ? 'Needs staff · future follow-ups stopped.' :
        action === 'approve' ? data.delivery?.mode === 'dry_run' ? 'Dry run recorded · no client email sent.' :
          data.delivery?.mode === 'redirect' ? `Test sent to ${data.delivery.delivered_recipient_email}.` : 'Email sent.' : '';
      setNotices((current) => ({ ...current, [id]: message }));
      if (action === 'approve') setExcluded((current) => ({ ...current, [draftKey(snapshot)]: true }));
      return data;
    } catch (error) {
      if (mounted.current && actionEpoch === epoch.current) {
        setErrors((current) => ({ ...current, [id]: errorText(error) }));
        if (error?.response?.status === 409 || action === 'approve') setChanged((current) => ({ ...current, [id]: true }));
      }
      return null;
    }
  };

  const onAction = async (snapshot, action, form) => {
    if (busyRef.current || changed[snapshot.lead.id]) return null;
    busyRef.current = true; setBusy(true); epoch.current += 1;
    try { return await performAction(snapshot, action, form); }
    finally { busyRef.current = false; if (mounted.current) setBusy(false); }
  };

  const onReload = async (snapshot) => {
    if (busyRef.current) return null;
    busyRef.current = true; setBusy(true); epoch.current += 1;
    try {
      const { data } = await fetchIntakeCommunication(snapshot.lead.id);
      if (!mounted.current) return null;
      if (data.conversation.batch_key !== snapshot.conversation.batch_key) {
        setUpdatesAvailable(true);
        setErrors((current) => ({ ...current, [snapshot.lead.id]: 'This conversation moved batches. Refresh this batch to continue.' }));
        return null;
      }
      replaceDetail(data);
      setChanged((current) => ({ ...current, [snapshot.lead.id]: false }));
      setErrors((current) => ({ ...current, [snapshot.lead.id]: '' }));
      return data;
    } catch (error) {
      if (mounted.current) setErrors((current) => ({ ...current, [snapshot.lead.id]: errorText(error) }));
      return null;
    } finally { busyRef.current = false; if (mounted.current) setBusy(false); }
  };

  const sendBatch = async (batch) => {
    if (busyRef.current || batch.conversations.some((detail) => changed[detail.lead.id] || editorsRef.current[detail.lead.id]?.editing)) return;
    const snapshots = batch.conversations.filter((detail) => ready(detail) && !excluded[draftKey(detail)]);
    if (!snapshots.length) return;
    busyRef.current = true; stopRef.current = false; setBusy(true); epoch.current += 1;
    let done = 0;
    let failed = false;
    const modes = { live: 0, redirect: 0, dry_run: 0 };
    setBatchNotices((current) => ({ ...current, [batch.key]: '' }));
    try {
      for (const snapshot of snapshots) {
        if (stopRef.current || !mounted.current) break;
        setProgress({ key: batch.key, done, total: snapshots.length, name: snapshot.conversation.name });
        const data = await performAction(snapshot, 'approve', undefined, batch.key);
        if (!data) { failed = true; break; }
        done += 1;
        modes[data.delivery?.mode || 'live'] += 1;
      }
      if (mounted.current) {
        const results = [modes.live ? `${modes.live} email${modes.live === 1 ? '' : 's'} sent` : '', modes.redirect ? `${modes.redirect} test${modes.redirect === 1 ? '' : 's'} sent` : '', modes.dry_run ? `${modes.dry_run} dry run${modes.dry_run === 1 ? '' : 's'} recorded · no client email sent` : ''].filter(Boolean).join('; ');
        const remaining = snapshots.length - done;
        setBatchNotices((current) => ({ ...current, [batch.key]: `${results || 'No emails sent'}.${remaining ? ` Stopped with ${remaining} remaining.${failed ? ' Review the flagged conversation before continuing.' : ''}` : ''}` }));
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) { setBusy(false); setProgress(null); }
    }
  };

  const filter = (values) => { if (canLeave()) updateParams({ lead: null, page: null, batch: boardRef.current.selected_batch_key, ...values }); };
  const refresh = () => { if (canLeave()) loadBoard(); };
  const total = Object.values(board.counts).reduce((sum, value) => sum + value, 0);
  const batch = board.batches[0];
  const eligible = batch?.conversations.filter(ready) || [];
  const included = eligible.filter((detail) => !excluded[draftKey(detail)]);
  const hasEditor = batch?.conversations.some((detail) => editors[detail.lead.id]?.editing);
  const hasChange = batch?.conversations.some((detail) => changed[detail.lead.id]);
  const mode = included[0]?.delivery_policy.mode || eligible[0]?.delivery_policy.mode || batch?.conversations[0]?.delivery_policy.mode;
  const sending = progress?.key === batch?.key;
  const bulkLabel = mode === 'dry_run' ? 'Record dry runs' : mode === 'redirect' ? 'Send tests' : included.length === eligible.length ? 'Send all' : 'Send selected';
  const conversationCount = batch?.conversations.length || 0;
  const currentIndex = Math.min(currentConversation, Math.max(0, conversationCount - 1));

  useEffect(() => { setCurrentConversation(0); }, [batch?.key, version]);
  useEffect(() => {
    const scroll = feedScrollRef.current;
    if (!scroll) return undefined;
    let frame = 0;
    const updateCurrent = () => {
      frame = 0;
      if (!scroll.clientHeight) return;
      const edge = scroll.getBoundingClientRect().top + parseFloat(window.getComputedStyle(scroll).scrollPaddingTop || 0) + 1;
      const cards = [...scroll.querySelectorAll(':scope > .communications-card')];
      const index = cards.findIndex((card) => card.getBoundingClientRect().bottom > edge);
      setCurrentConversation(index < 0 ? Math.max(0, cards.length - 1) : index);
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(updateCurrent); };
    scroll.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    updateCurrent();
    return () => {
      scroll.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [batch, version, loading]);

  const goToConversation = (index) => {
    const scroll = feedScrollRef.current;
    const target = scroll?.querySelectorAll(':scope > .communications-card')[index];
    if (!target) return;
    const padding = parseFloat(window.getComputedStyle(scroll).scrollPaddingTop || 0);
    const top = scroll.scrollTop + target.getBoundingClientRect().top - scroll.getBoundingClientRect().top - padding;
    scroll.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    setCurrentConversation(index);
  };

  return <div className="intake-page intake-communications" onClickCapture={(event) => {
    if (event.target.closest('.intake-view-switch__item') && !canLeave()) { event.preventDefault(); event.stopPropagation(); }
  }}>
    <header className="communications-heading"><div><div className="communications-eyebrow">Intake</div><h1>Communications</h1></div><IntakeViewSwitch activeView="communications" /></header>
    <div className="communications-toolbar">
      <label className="communications-batch-selector"><span>Batch</span><select aria-label="Communication batch" value={board.selected_batch_key || ''} disabled={busy || loading || !board.batch_options.length} onChange={(event) => filter({ batch: event.target.value })}>
        <option value="" disabled>Choose a batch</option>{board.batch_options.map((option) => <option key={option.key} value={option.key}>{option.label}{option.opened_at ? ` · ${batchDate(option.opened_at)}` : ''}</option>)}
      </select></label>
      <div className="communications-filters"><select aria-label="Conversation owner" value={scope} disabled={busy || loading} onChange={(event) => filter({ scope: event.target.value })}><option value="all">All owners</option><option value="mine">Assigned to me</option><option value="unassigned">Unassigned</option></select>
        <label className="communications-search"><AppIcon icon="pi pi-search" /><input aria-label="Search conversations" placeholder="Find a prospect" value={searchText} disabled={busy} onChange={(event) => setSearchText(event.target.value)} /></label>
        <button type="button" className="communications-icon-button" aria-label="Refresh communications" disabled={busy || loading} onClick={refresh}><AppIcon icon="pi pi-refresh" /></button>
      </div>
      <nav className="communications-tabs" aria-label="Communication status">{VIEWS.map(([key, label]) => <button type="button" key={key} aria-pressed={view === key} disabled={busy || loading} onClick={() => filter({ view: key })}>{label}<span>{key === 'all' ? total : board.counts[key] || 0}</span></button>)}</nav>
    </div>
    {loadError ? <div role="alert" className="communications-error">{loadError}<button type="button" className="intake-button" disabled={busy} onClick={refresh}>Try again</button></div> : null}
    {updatesAvailable ? <div className="communications-updates" role="status"><span><AppIcon icon="pi pi-refresh" />New conversation updates</span><button type="button" className="intake-button" disabled={busy || loading} onClick={refresh}>Refresh batch</button></div> : null}
    <div className="communications-feed" aria-label="Batch communication feed" aria-busy={loading}>
      {loading ? <div className="communications-empty" role="status">Loading batch communications…</div> : !batch ? <div className="communications-empty"><AppIcon icon="pi pi-check-circle" /><strong>No conversations here.</strong><span>New communications will appear in their batch.</span></div> : <section key={`${batch.key}:${version}`} className="communications-batch" aria-label={batch.label}>
          <header className="communications-batch__heading"><div className="communications-batch__title"><div><h2>{batch.label}</h2><span>{[batchDate(batch.opened_at), `${batch.conversations.length} conversation${batch.conversations.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}</span></div><span className="communications-batch__ready"><AppIcon icon="pi pi-envelope" />{eligible.length} ready</span></div>
            <div className="communications-review-position" role="group" aria-label="Conversation navigation"><button type="button" className="intake-button" aria-label="Previous conversation" disabled={!conversationCount || currentIndex === 0} onClick={() => goToConversation(currentIndex - 1)}><AppIcon icon="pi pi-chevron-up" /></button><span aria-live="polite" aria-atomic="true">{conversationCount ? currentIndex + 1 : 0} / {conversationCount}</span><button type="button" className="intake-button" aria-label="Next conversation" disabled={!conversationCount || currentIndex >= conversationCount - 1} onClick={() => goToConversation(currentIndex + 1)}><AppIcon icon="pi pi-chevron-down" /></button></div>
            {sending ? <div className="communications-batch__progress" role="status"><div><strong>{progress.done} / {progress.total} completed</strong><span>{progress.stopping ? 'Stopping after the current email…' : progress.name}</span><progress value={progress.done} max={progress.total} aria-label="Batch send progress" /></div><button type="button" className="intake-button" disabled={progress.stopping} onClick={() => { stopRef.current = true; setProgress((current) => ({ ...current, stopping: true })); }}><AppIcon icon="pi pi-stop" />Stop remaining</button></div> : <div className="communications-batch__send"><span className="communications-mode"><AppIcon icon={mode === 'live' ? 'pi pi-send' : 'pi pi-eye'} />{mode === 'dry_run' ? 'Dry run · no client email' : mode === 'redirect' ? `Test to ${included[0]?.delivery_policy.delivered_recipient_email || batch.conversations[0]?.delivery_policy.delivered_recipient_email || 'unconfigured recipient'}` : 'Sends to clients'}</span><button type="button" className="intake-button intake-button--primary" disabled={busy || loading || !included.length || hasEditor || hasChange || !!loadError} onClick={() => sendBatch(batch)}><AppIcon icon="pi pi-send" />{bulkLabel}<span className="communications-send-count">{included.length}</span></button></div>}
            {hasEditor ? <div className="communications-batch__hint">Save or cancel edits before sending this batch.</div> : hasChange ? <div className="communications-batch__hint">Reload changed conversations before sending this batch.</div> : null}
          </header>
          {batchNotices[batch.key] ? <div role="status" className="communications-notice">{batchNotices[batch.key]}</div> : null}
          <div className="communications-batch__scroll" ref={feedScrollRef} tabIndex={0} aria-label={`${batch.label} conversations`}>{!batch.conversations.length ? <div className="communications-empty"><AppIcon icon="pi pi-check-circle" /><strong>No matching conversations in this batch.</strong><span>{scope === 'mine' ? 'Try All owners to see the shared queue.' : 'Adjust the status or search to see more.'}</span></div> : batch.conversations.map((detail) => <IntakeCommunicationCard key={detail.lead.id} detail={detail} included={!excluded[draftKey(detail)]} changed={!!changed[detail.lead.id]} error={errors[detail.lead.id]} notice={notices[detail.lead.id]} busy={busy || loading} onInclude={(snapshot, value) => setExcluded((current) => ({ ...current, [draftKey(snapshot)]: !value }))} onAction={onAction} onReload={onReload} onEditorChange={onEditorChange} />)}</div>
        </section>}
    </div>
  </div>;
}
